import React from 'react';
import { Checkbox, ComboBox, DirectionalHint, IComboBox, IComboBoxOption, Text } from '@fluentui/react';
import { ApplicationContext } from '../contexts/ApplicationContext';
import { IStakeholder } from '../models/IStakeholder';

const MinSearchLength = 2;
const HintStyles = { root: { padding: "10px 12px", color: "#605e5c" } };

interface IStakeholderSelectionProps
{
    label: string;
    disabled: boolean;
    OnStakeholderSelected: (stakeholder: IStakeholder) => void;
    canFetchAll: boolean;
}

export const StakeholderSelection: React.FunctionComponent<IStakeholderSelectionProps> = (props: React.PropsWithChildren<IStakeholderSelectionProps>) => {    
    const { Services } = React.useContext(ApplicationContext);

    const comboBoxRef = React.useRef<IComboBox>(null);
    const [items, setItems] = React.useState<IStakeholder[]>([]);
    const [selected, setSelected] = React.useState<IStakeholder | undefined>(undefined);
    const [selectedKey, setSelectedKey] = React.useState<string | number | undefined>(undefined);
    const [searchText, setSearchText] = React.useState<string>("");
    const [fetchAll, setFetchAll] = React.useState<boolean>(false);
      React.useEffect(() => {    
        Fetch();
      }, [fetchAll]);

    const Fetch = async () => {
        Services?.Stakeholder.GetStakeholders(fetchAll).then((stakeholders: IStakeholder[]) => {
            setItems(stakeholders);
        }); 
    }      

    const FetchFull = async (personId: string | number | undefined ) => {
      setSelected(await Services?.Stakeholder.GetStakeholder(parseInt(personId + "")));
    }   
    
    React.useEffect(() => {
      if(selected !== undefined)
      {
        props.OnStakeholderSelected(selected as IStakeholder);
      }
      
    }, [selected?.personID]);

    const canSearch = searchText.trim().length >= MinSearchLength;

    const allOptions = React.useMemo(() => items.map((val) => { return { key: val.personID, text: `${val.personID} - ${val.firstName} ${val.lastName}` } as IComboBoxOption }), [items]);

    // Søg i både person-ID og navn - alle ord i søgeteksten skal indgå et sted i teksten.
    const matches = React.useMemo(() => {
      if (!canSearch) {
        return [];
      }
      const terms = searchText.toLowerCase().split(/\s+/).filter(t => t.length > 0);
      return allOptions.filter(o => terms.every(t => o.text.toLowerCase().includes(t)));
    }, [allOptions, searchText, canSearch]);

    // Den valgte person beholdes altid i listen, så ComboBox kan vise dens tekst.
    const options = React.useMemo(() => {
      const selectedOption = allOptions.find(o => o.key === selectedKey);
      return selectedOption === undefined || matches.includes(selectedOption) ? matches : [selectedOption, ...matches];
    }, [allOptions, matches, selectedKey]);

    // Har brugeren navigeret i listen med piletasterne (eller holder musen over en person), håndterer ComboBox selv Enter
    const hasNavigatedRef = React.useRef<boolean>(false);

    const Select = (option: IComboBoxOption) => {
      setSearchText("");
      setSelectedKey(option.key);
      FetchFull(option.key);
    }

    const OnKeyDownCapture = (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
        hasNavigatedRef.current = true;
      }
      else if (ev.key === "Enter" && !hasNavigatedRef.current && matches.length > 0 && document.querySelector(".ms-ComboBox-option:hover") === null) {
        // Eventet sendes videre, så ComboBox selv rydder søgeteksten og lukker listen
        Select(matches[0]);
      }
    }

    const OnInputValueChange = (text: string) => {
      hasNavigatedRef.current = false;
      setSearchText(text);
      if (text.trim().length >= MinSearchLength) {
        comboBoxRef.current?.focus(true);
      } else {
        comboBoxRef.current?.dismissMenu();
      }
    }

    return <>
    <div onKeyDownCapture={OnKeyDownCapture}>
    <ComboBox
      componentRef={comboBoxRef}
      disabled={props.disabled}
      key={`stakeholderSelection_${props.label}`}
      label={props.label}
      allowFreeInput
      autoComplete="off"
      selectedKey={selectedKey ?? null}
      options={options}
      useComboBoxAsMenuWidth
      calloutProps={{ directionalHint: DirectionalHint.bottomLeftEdge, directionalHintFixed: true }}
      styles={{ optionsContainerWrapper: { maxHeight: 300 } }}
      onRenderLowerContent={() => canSearch
        ? (matches.length === 0 ? <Text block styles={HintStyles}>Ingen personer fundet</Text> : null)
        : <Text block styles={HintStyles}>Skriv mindst {MinSearchLength} tegn for at søge</Text>}
      onInputValueChange={OnInputValueChange}
      onMenuDismissed={() => setSearchText("")}
      onChange={(ev, option: IComboBoxOption | undefined) => {
        if (option !== undefined) {
          Select(option);
        } else {
          setSearchText("");
        }
      }}
    />
    </div>
    {props.canFetchAll && <Checkbox styles={{root: { marginTop:"5px"}}} label="Vælg blandt alle personer (også personer uden andele i aktuelle selskab)" onChange={(ev, checked) => { setFetchAll(checked as boolean); }} />}
    </>
}

export default StakeholderSelection;